/**
 * Centralized date formatting utilities for the Bunny Boba apps.
 * All apps should use these functions to ensure consistent date display
 * based on the system settings (dateFormat, timeFormat).
 */

const pad = (n) => String(n).padStart(2, "0");

/**
 * Supported date format patterns and their formatters
 */
const DATE_FORMATTERS = {
  "DD/MM/YYYY": (d) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`,
  "MM/DD/YYYY": (d) => `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`,
  "YYYY-MM-DD": (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
};

/**
 * Time format patterns
 */
const TIME_FORMATTERS = {
  "12h": (d) => {
    let hours = d.getHours();
    const minutes = pad(d.getMinutes());
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    return `${hours}:${minutes} ${ampm}`;
  },
  "24h": (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`,
};

/**
 * Normalize various date inputs to a Date object.
 * Handles: Date, ISO string, Firestore Timestamp (with _seconds or toDate()),
 * unix timestamp (number), and null/undefined.
 *
 * @param {any} value - The date value to normalize
 * @returns {Date|null} - A Date object or null if invalid
 */
export function normalizeDate(value) {
  if (!value) return null;

  // Already a Date
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  // Firestore Timestamp with toDate() method
  if (typeof value.toDate === "function") {
    return value.toDate();
  }

  // Firestore Timestamp-like object with _seconds
  if (typeof value._seconds === "number") {
    return new Date(value._seconds * 1000);
  }

  // Unix timestamp (number of milliseconds or seconds)
  if (typeof value === "number") {
    // If it looks like seconds (< year 3000 in ms would be ~31 billion)
    if (value < 100000000000) {
      return new Date(value * 1000);
    }
    return new Date(value);
  }

  // ISO string or other parseable string
  if (typeof value === "string") {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
}

/**
 * Format a date value according to the specified format.
 *
 * @param {any} value - Date, ISO string, Firestore Timestamp, or unix timestamp
 * @param {string} fmt - Format pattern: "DD/MM/YYYY", "MM/DD/YYYY", or "YYYY-MM-DD"
 * @returns {string} - Formatted date string, or empty string if invalid
 */
export function formatDate(value, fmt = "DD/MM/YYYY") {
  const date = normalizeDate(value);
  if (!date) return "";

  const formatter = DATE_FORMATTERS[fmt];
  if (!formatter) {
    console.warn(`[formatDate] Unknown format "${fmt}", falling back to DD/MM/YYYY`);
    return DATE_FORMATTERS["DD/MM/YYYY"](date);
  }

  return formatter(date);
}

/**
 * Format a time value according to the specified format.
 *
 * @param {any} value - Date, ISO string, Firestore Timestamp, or unix timestamp
 * @param {string} timeFmt - Time format: "12h" or "24h"
 * @returns {string} - Formatted time string, or empty string if invalid
 */
export function formatTime(value, timeFmt = "12h") {
  const date = normalizeDate(value);
  if (!date) return "";

  const formatter = TIME_FORMATTERS[timeFmt];
  if (!formatter) {
    console.warn(`[formatTime] Unknown format "${timeFmt}", falling back to 12h`);
    return TIME_FORMATTERS["12h"](date);
  }

  return formatter(date);
}

/**
 * Format a datetime value with both date and time.
 *
 * @param {any} value - Date, ISO string, Firestore Timestamp, or unix timestamp
 * @param {string} dateFmt - Date format pattern
 * @param {string} timeFmt - Time format: "12h" or "24h"
 * @returns {string} - Formatted datetime string, or empty string if invalid
 */
export function formatDateTime(value, dateFmt = "DD/MM/YYYY", timeFmt = "12h") {
  const date = normalizeDate(value);
  if (!date) return "";

  return `${formatDate(date, dateFmt)} ${formatTime(date, timeFmt)}`;
}

/**
 * Get the start of today (00:00:00.000)
 * @returns {Date}
 */
export function startOfToday() {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now;
}

/**
 * Get the end of today (23:59:59.999)
 * @returns {Date}
 */
export function endOfToday() {
  const now = new Date();
  now.setHours(23, 59, 59, 999);
  return now;
}

/**
 * Get the start of a given date (00:00:00.000)
 * @param {Date} date
 * @returns {Date}
 */
export function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Get the end of a given date (23:59:59.999)
 * @param {Date} date
 * @returns {Date}
 */
export function endOfDay(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

export default {
  normalizeDate,
  formatDate,
  formatTime,
  formatDateTime,
  startOfToday,
  endOfToday,
  startOfDay,
  endOfDay,
};
